import { useState, useEffect } from 'react';
import api from '../services/api';
import { ArrowLeft, ChevronRight, Loader2, PoundSterling, Plus } from 'lucide-react';
import { toast } from 'sonner';

const PRICING_URL = `${process.env.REACT_APP_BACKEND_URL}/api/pricing`;
const ORANGE = '#ee7203';

const money = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const perLabel = (u) => (u === 'M2' ? 'm²' : u === 'M' ? 'm' : 'each');

/**
 * "Supplier" tab of the Create Quote View Price Lists slide-out. Same look and behaviour as the B29 price list panel: the
 * suppliers whose price lists are switched ON in Settings > Pricing, click one to see its products by category. Each product
 * shows its cost (list price less the group's discount and rebate); the "+" puts it on the quote and fills the line's cost
 * calculator from Settings > Pricing exactly as picking its stock code by hand does.
 */
const SupplierPriceListPanel = ({ onAddProduct = null }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`${PRICING_URL}/quote-catalog/suppliers`);
        if (alive) setSuppliers(res.data?.suppliers || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Failed to load the supplier price lists');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openSupplier = async (supplier) => {
    setSelected(supplier);
    setCatalog(null);
    setCatalogLoading(true);
    try {
      const res = await api.get(`${PRICING_URL}/quote-catalog/suppliers/${supplier.id}/products`);
      setCatalog(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load this supplier's products");
      setSelected(null);
    } finally {
      setCatalogLoading(false);
    }
  };

  const backToSuppliers = () => { setSelected(null); setCatalog(null); };

  return (
    <div className="h-full flex flex-col" data-testid="quote-supplier-pricelist-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        {selected && (
          <button onClick={backToSuppliers} className="text-gray-400 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 flex-shrink-0" title="Back to all suppliers" data-testid="quote-supplier-pricelist-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
          <PoundSterling className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight truncate">{selected ? selected.name : 'Supplier Price Lists'}</h3>
          <p className="text-[11px] text-gray-500 truncate">
            {selected ? 'All products by category — cost after discount and rebate' : `${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'} switched on — click one to see its products`}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
        {!selected && (
          loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading suppliers…</span></div>
          ) : error ? (
            <div className="text-center py-12 text-sm text-red-600">{error}</div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <PoundSterling className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm">No supplier price lists are switched on yet — turn a supplier's categories ON in Settings &gt; Pricing.</p>
            </div>
          ) : (
            suppliers.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openSupplier(s)}
                className="w-full flex items-center gap-3 rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-left hover:bg-orange-50 hover:border-orange-400 transition-colors"
                data-testid={`quote-supplier-pricelist-supplier-${s.id}`}
              >
                <span className="flex-1 min-w-0 font-bold text-sm text-gray-900 truncate">{s.name}</span>
                <span className="text-[11px] font-semibold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 flex-shrink-0">{s.product_count} product{s.product_count === 1 ? '' : 's'}</span>
                <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0" />
              </button>
            ))
          )
        )}

        {selected && (
          catalogLoading || !catalog ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading products…</span></div>
          ) : (catalog.categories || []).length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">No switched-on products for this supplier.</div>
          ) : (
            catalog.categories.map((cat) => (
              <section key={cat.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white" data-testid={`quote-supplier-pricelist-category-${cat.id}`}>
                <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: ORANGE }}>
                  <span className="text-sm font-bold text-white uppercase tracking-wide truncate">{cat.name}</span>
                  {(cat.discount_pct > 0 || cat.rebate_pct > 0) && (
                    <span className="text-[11px] text-white/90 flex-shrink-0">
                      {cat.discount_pct > 0 ? `${cat.discount_pct}% disc` : ''}{cat.discount_pct > 0 && cat.rebate_pct > 0 ? ' · ' : ''}{cat.rebate_pct > 0 ? `${cat.rebate_pct}% rebate` : ''}
                    </span>
                  )}
                  <span className="text-xs text-white/80 ml-auto flex-shrink-0">{cat.products.length} product{cat.products.length === 1 ? '' : 's'}</span>
                </div>
                {cat.products.map((p) => {
                  const code = p.stock_code || p.supplier_product_code || '—';
                  return (
                    <div key={p.id} className="grid grid-cols-[130px_minmax(0,1fr)_150px] gap-4 items-center px-4 py-3 border-t border-gray-100 hover:bg-orange-50/40">
                      <div className="min-w-0">
                        <div className="font-mono font-bold text-sm text-gray-900 break-all">{code}</div>
                        {!p.stock_code && <div className="text-[10px] font-semibold text-amber-600">no stock code yet</div>}
                        {onAddProduct && (
                          <button
                            type="button"
                            onClick={() => onAddProduct(p)}
                            title="Add to the quote"
                            className="mt-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full text-white shadow-sm hover:shadow-md active:scale-95 transition-all"
                            style={{ backgroundColor: ORANGE }}
                            data-testid={`quote-supplier-pricelist-add-${p.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800">{p.description || '—'}</p>
                        {p.updated_label && <p className="text-xs text-gray-400">Update: {p.updated_label}</p>}
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold text-gray-900 tabular-nums">{money(p.net_cost)}</div>
                        <div className="text-xs text-gray-400">per {perLabel(p.quote_uom)}</div>
                        {(p.discount_pct > 0 || p.rebate_pct > 0) && <div className="text-[11px] text-gray-400">List {money(p.list_price)}</div>}
                      </div>
                    </div>
                  );
                })}
              </section>
            ))
          )
        )}
      </div>
    </div>
  );
};

export default SupplierPriceListPanel;
